import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaCalendar,
  FaClock,
  FaEye,
  FaUser,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaDownload,
  FaFilter,
  FaImage,
  FaVideo,
  FaTag,
  FaGlobe,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaPlay,
  FaPause,
  FaUsers,
  FaChartBar,
  FaTrophy,
  FaStar,
  FaTimes,
  FaInfoCircle,
  FaMicrophone,
  FaCamera,
  FaExclamationTriangle,
  FaCalculator
} from "react-icons/fa";

const CompletedPost = () => {
  const [ads, setAds] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredAds, setFilteredAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMedia, setPreviewMedia] = useState({ url: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedProofDetails, setExpandedProofDetails] = useState({});
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionDetails, setCompletionDetails] = useState(null);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [shouldRefund, setShouldRefund] = useState(true); // Admin choice to refund or not
  const rowsPerPage = 50;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompletedAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/final/completed/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched completed Ads Data:", response.data);
        console.log("Full API Response Structure:", JSON.stringify(response.data, null, 2));
        const adsData = Array.isArray(response.data.data) ? response.data.data : [];
        console.log("Enhanced API Response - Total Ads:", adsData.length);

        // Debug: Check if ads array exists but is empty
        if (response.data.data && response.data.data.length === 0) {
          console.log("API returned empty ads array");
        }

        // Debug: Log the actual structure
        if (adsData.length > 0) {
          console.log("Sample ad structure:", adsData[0]);
          console.log("Sample ad proofs:", adsData[0].proofs);
        }

        adsData.forEach((ad, index) => {
          console.log(`Ad ${index + 1}:`, {
            adId: ad.adId,
            status: ad.status,
            totalProofs: ad.proofs?.length || 0,
            completedProofs: ad.proofs?.filter(p => p.status === "completed").length || 0,
          });
        });
        setAds(adsData);
      } catch (err) {
        setError("Failed to fetch completed ads");
      } finally {
        setLoading(false);
      }
    };
    fetchCompletedAds();
  }, []);

  useEffect(() => {
    // Sort ads by updatedAt descending (newest first)
    const sortedAds = [...ads].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
    if (!search) {
      setFilteredAds(sortedAds);
      setCurrentPage(1);
      return;
    }
    const lowerSearch = search.toLowerCase();
    const filtered = sortedAds.filter(ad => {
      const adId = ad.adId?.toLowerCase() || '';
      const mediaDescription = ad.mediaDescription?.toLowerCase() || '';
      const state = ad.adState?.toLowerCase() || '';
      const city = ad.adCity?.toLowerCase() || '';
      const dateStr = ad.updatedAt ? new Date(ad.updatedAt).toLocaleDateString("en-GB") : '';
      return (
        adId.includes(lowerSearch) ||
        mediaDescription.includes(lowerSearch) ||
        state.includes(lowerSearch) ||
        city.includes(lowerSearch) ||
        dateStr.includes(lowerSearch)
      );
    });
    setFilteredAds(filtered);
    setCurrentPage(1);
  }, [ads, search]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-emerald-500 animate-spin"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-teal-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-b-cyan-500 animate-spin" style={{ animationDuration: '2s' }}></div>
        </div>
        <p className="text-lg font-semibold text-gray-700">Loading Paid Completed Conferences...</p>
        <p className="text-sm text-gray-500">Please wait while we fetch the data</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center border border-red-200">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaTimesCircle className="text-red-500 text-2xl" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const paginatedAds = filteredAds.slice(startIdx, endIdx);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Function to get completion details before showing confirmation
  const getCompletionDetails = async (ad) => {
    try {
      console.log("🔍 Frontend - Ad object received:", ad);
      console.log("🔍 Frontend - Ad.adId:", ad.adId);
      console.log("🔍 Frontend - Ad._id:", ad._id);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/advertisement/${ad.adId}/completion-details`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setCompletionDetails(response.data.data);
        setShouldRefund(true); // Reset to default (Yes) when opening modal
        setShowCompletionModal(true);
      }
    } catch (error) {
      console.error("Error fetching completion details:", error);
      alert("Error fetching completion details: " + (error.response?.data?.message || "Failed to fetch details"));
    }
  };

  // Function to confirm completion
  const confirmCompletion = async () => {
    if (!completionDetails) return;

    setCompletionLoading(true);
    try {
      console.log("🚀 Frontend - Completion details:", completionDetails);
      console.log("🚀 Frontend - Completion details.adId:", completionDetails.adId);

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/mark/advertisement/${completionDetails.adId}/completed`,
        { shouldRefund: shouldRefund }, // Send admin's refund choice
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        let message = "Advertisement completed successfully!";

        if (data.refundProcessed && data.totalRefundAmount > 0) {
          message += `\n\n✅ Refund Processed:\n- Total Refund Amount: ₹${data.totalRefundAmount}\n- Refunded Reporters: ${data.refundedReporters}\n- Rejected Reporters: ${data.rejectedReporters}`;

          if (data.refundDetails && data.refundDetails.length > 0) {
            message += `\n\nRefund Details:`;
            data.refundDetails.forEach((refund, index) => {
              message += `\n${index + 1}. ${refund.reporterName}: ₹${refund.amount} - ${refund.reason}`;
            });
          }
        } else if (data.refundSkipped) {
          message += `\n\n⚠️ Refund Skipped (Admin Choice):\n- Potential Refund Amount: ₹${data.refundSkipped.amount}\n- Reason: ${data.refundSkipped.reason}\n- Incomplete Reporters: ${data.refundSkipped.incompleteReporters}`;
        }

        if (data.completedReporters > 0) {
          message += `\n\n✅ Completed Work:\n- Successfully Completed: ${data.completedReporters} reporter(s)`;
        }

        alert(message);
        setShowCompletionModal(false);
        setCompletionDetails(null);
        setShouldRefund(true); // Reset to default
        window.location.reload();
      }
    } catch (error) {
      console.error("Error completing advertisement:", error);
      alert("Error: " + (error.response?.data?.message || "Failed to complete advertisement"));
    } finally {
      setCompletionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Modern Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <FaMicrophone className="text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Completed Paid Advertisements</h1>
                <p className="text-emerald-100 mt-2">View completed paid advertisements and their proof details</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors backdrop-blur-sm"
                onClick={() => navigate("/admin/pendingposts")}
              >
                <FaClock />
                <span>Pending</span>
              </button>
              <button
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors backdrop-blur-sm"
                onClick={() => navigate("/admin/runningposts")}
              >
                <FaPlay />
                <span>Running</span>
              </button>
              <button
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors backdrop-blur-sm"
                onClick={() => navigate("/admin/approvedposts")}
              >
                <FaCheckCircle />
                <span>Approved</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                  placeholder="Search by Conference ID, Topic, State, City, or Date (dd/mm/yyyy)"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            <button
              className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-4 py-3 rounded-xl transition-colors"
              onClick={() => setSearch('')}
            >
              <FaTimes />
              <span>Clear</span>
            </button>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <FaChartBar />
              <span>{filteredAds.length} advertisements found</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    S.No.
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaMicrophone className="w-4 h-4" />
                      Conference ID
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaTag className="w-4 h-4" />
                      Topic
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaCalendar className="w-4 h-4" />
                      Date & Time
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt className="w-4 h-4" />
                      Location
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaUsers className="w-4 h-4" />
                      Completion Stats
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="w-4 h-4" />
                      Status
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaEye className="w-4 h-4" />
                      Actions
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedAds.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center">
                          <FaCheckCircle className="w-10 h-10 text-emerald-500" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">No Completed Advertisements Found</h3>
                          <p className="text-gray-500">There are no completed paid advertisements at the moment</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedAds.map((ad, index) => (
                    <tr key={ad._id || ad.adId || `ad-${index}`} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {startIdx + index + 1}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                              {ad.adId?.charAt(0)?.toUpperCase() || 'A'}
                            </span>
                          </div>
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {ad.adId}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <p className="text-sm font-medium text-gray-900 truncate" title={ad.mediaDescription}>
                            {ad.mediaDescription}
                          </p>
                          <p className="text-xs text-gray-500 truncate" title={ad.mediaType}>
                            {ad.mediaType}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {ad.updatedAt ? new Date(ad.updatedAt).toLocaleDateString('en-GB') : 'N/A'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {ad.updatedAt ? new Date(ad.updatedAt).toLocaleTimeString('en-GB') : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">{ad.adCity}, {ad.adState}</div>
                          <div className="text-xs text-gray-500">Advertisement Location</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                              <FaUsers className="w-3 h-3 mr-1" />
                              {ad.proofs?.filter(p => p.status === "completed").length || 0}/{ad.requiredReporter || 0}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {(() => {
                              const completed = ad.proofs?.filter(p => p.status === "completed").length || 0;
                              const total = ad.requiredReporter || 0;
                              const percentage = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
                              return `${percentage}% Complete`;
                            })()}
                          </div>
                          <div className="text-xs text-gray-400">
                            Required Reporters: {ad.requiredReporter || 0} |
                            Completed: {ad.proofs?.filter(p => p.status === "completed").length || 0} |
                            Submitted: {ad.proofs?.filter(p => p.status === "submitted").length || 0} |
                            Rejected: {ad.proofs?.filter(p => p.status === "rejected").length || 0}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
                            <FaCheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </span>
                          <span className="text-xs text-gray-500">
                            {ad.updatedAt ? new Date(ad.updatedAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-4 py-2 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                          onClick={() => {
                            setSelectedAd(ad);
                            setShowModal(true);
                          }}
                        >
                          <FaEye />
                          <span>View Details</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-12">
            <nav className="inline-flex items-center space-x-2 bg-white rounded-xl shadow-lg border border-gray-200 p-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200'
                  }`}
              >
                <span className="flex items-center gap-2">
                  <FaArrowLeft className="w-4 h-4" />
                  Previous
                </span>
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  const isActive = pageNum === currentPage;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 ${isActive
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200'
                  }`}
              >
                <span className="flex items-center gap-2">
                  Next
                  <FaArrowRight className="w-4 h-4" />
                </span>
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Media Preview Modal */}
      {showPreviewModal && previewMedia.url && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => {
          setShowPreviewModal(false);
          setPreviewMedia({ url: '', type: '' });
        }}>
          <div className="max-w-4xl max-h-[90vh] relative bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {previewMedia.type === 'video' ? <FaVideo /> : <FaImage />}
                Media Preview
              </h3>
              <button
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewMedia({ url: '', type: '' });
                }}
              >
                <FaTimesCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {previewMedia.type === 'video' ? (
                <video
                  src={previewMedia.url}
                  className="max-h-[70vh] max-w-full rounded-lg"
                  controls
                  autoPlay
                />
              ) : (
                <img
                  src={previewMedia.url}
                  alt="Preview"
                  className="max-h-[70vh] max-w-full rounded-lg object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showModal && selectedAd && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-t-2xl">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FaCheckCircle />
                Advertisement Details
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FaTimesCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Basic Information Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Advertisement ID</label>
                    <p className="text-gray-800 font-mono">{selectedAd.adId}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Status</label>
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                      Completed
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Media Description</label>
                    <p className="text-gray-800">{selectedAd.mediaDescription}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Media Type</label>
                    <p className="text-gray-800">{selectedAd.mediaType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Created Date</label>
                    <p className="text-gray-800">{selectedAd.createdAt ? new Date(selectedAd.createdAt).toLocaleDateString('en-GB') : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Updated Date</label>
                    <p className="text-gray-800">{selectedAd.updatedAt ? new Date(selectedAd.updatedAt).toLocaleDateString('en-GB') : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Location Information Section */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Location Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">State</label>
                    <p className="text-gray-800">{selectedAd.adState}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">City</label>
                    <p className="text-gray-800">{selectedAd.adCity}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Targeting Type</label>
                    <p className="text-gray-800">{selectedAd.allStates ? 'All States' : 'Specific States'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">User Type</label>
                    <p className="text-gray-800">{selectedAd.userType || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Submitted By Section */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Submitted By</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Advertiser Name</label>
                    <p className="text-gray-800">
                      {typeof selectedAd.owner === 'object' && selectedAd.owner?.name
                        ? selectedAd.owner.name
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Organization</label>
                    <p className="text-gray-800">
                      {typeof selectedAd.owner === 'object' && selectedAd.owner?.organization
                        ? selectedAd.owner.organization
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Email</label>
                    <p className="text-gray-800">
                      {typeof selectedAd.owner === 'object' && selectedAd.owner?.email
                        ? selectedAd.owner.email
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">IINSAF ID</label>
                    <p className="text-gray-800">
                      {typeof selectedAd.owner === 'object' && selectedAd.owner?.iinsafId
                        ? selectedAd.owner.iinsafId
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Completed Reporters Section */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FaUsers />
                    Completed Proofs ({selectedAd.proofs?.filter(p => p.status === "completed").length || 0})
                  </h3>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Completion:</span>
                      <span className="font-semibold text-blue-600">
                        {selectedAd.proofs?.filter(p => p.status === "completed").length || 0}/{selectedAd.proofs?.length || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Percentage:</span>
                      <span className="font-semibold text-green-600">
                        {(() => {
                          const completed = selectedAd.proofs?.filter(p => p.status === "completed").length || 0;
                          const total = selectedAd.proofs?.length || 0;
                          const percentage = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
                          return `${percentage}%`;
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Accepted:</span>
                      <span className="font-semibold text-blue-600">
                        {selectedAd.proofs?.filter(p => p.status === "accepted").length || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Rejected:</span>
                      <span className="font-semibold text-red-600">
                        {selectedAd.proofs?.filter(p => p.status === "rejected").length || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Completion Date:</span>
                      <span className="font-semibold text-blue-600">
                        {selectedAd.updatedAt ? new Date(selectedAd.updatedAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800`}>
                        ✅ Advertisement Completed
                      </span>
                    </div>
                  </div>
                </div>

                {selectedAd.proofs && selectedAd.proofs.filter(p => p.status === "completed").length > 0 ? (
                  <div className="overflow-y-auto max-h-80 space-y-3 py-2">
                    {selectedAd.proofs.filter(p => p.status === "completed").map((proof, index) => (
                      <div key={proof.reporterId || proof._id || `proof-${index}`} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        {/* Basic Reporter Info - Always Visible */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                            <label className="block text-xs font-medium text-blue-600 mb-1">Reporter ID</label>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-bold">
                                  {proof.reporterId?.charAt(0)?.toUpperCase() || 'R'}
                                </span>
                              </div>
                              <p className="text-sm font-bold text-gray-800 truncate">{proof.reporterId}</p>
                            </div>
                          </div>

                          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                            <label className="block text-xs font-medium text-purple-600 mb-1">IINSAF ID</label>
                            <p className="text-sm font-bold text-gray-800">{proof.iinsafId || 'N/A'}</p>
                          </div>

                          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                            <label className="block text-xs font-medium text-green-600 mb-1">Channel Name</label>
                            <p className="text-sm font-bold text-gray-800">{proof.channelName || 'N/A'}</p>
                          </div>

                          <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
                            <label className="block text-xs font-medium text-orange-600 mb-1">Platform</label>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-gray-800">{proof.platform || 'N/A'}</span>
                              {proof.platform === 'Youtube' && <span className="text-red-500 text-lg">📺</span>}
                              {proof.platform === 'Instagram' && <span className="text-pink-500 text-lg">📷</span>}
                              {proof.platform === 'Facebook' && <span className="text-blue-500 text-lg">👥</span>}
                              {proof.platform === 'Twitter' && <span className="text-blue-400 text-lg">🐦</span>}
                            </div>
                          </div>
                        </div>

                        {/* Status & Timeline - Always Visible */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-3 border border-emerald-200">
                            <label className="block text-xs font-medium text-emerald-600 mb-1">Status</label>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-800 border border-green-200">
                              <FaCheckCircle className="w-4 h-4 mr-2" />
                              Completed
                            </span>
                          </div>

                          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                            <label className="block text-xs font-medium text-blue-600 mb-1">Video Duration</label>
                            <p className="text-lg font-bold text-gray-800">{proof.duration || 'N/A'}</p>
                          </div>

                          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                            <label className="block text-xs font-medium text-purple-600 mb-1">User Role</label>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${proof.userRole === 'Influencer'
                              ? 'bg-pink-100 text-pink-800 border border-pink-200'
                              : 'bg-purple-100 text-purple-800 border border-purple-200'
                              }`}>
                              {proof.userRole || 'Reporter'}
                            </span>
                          </div>
                        </div>

                        {/* View More Details Button */}
                        <div className="mb-4">
                          <button
                            onClick={() => {
                              const currentExpanded = expandedProofDetails[proof.reporterId] || false;
                              setExpandedProofDetails({
                                ...expandedProofDetails,
                                [proof.reporterId]: !currentExpanded
                              });
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                          >
                            <FaEye className="w-4 h-4" />
                            {expandedProofDetails[proof.reporterId] ? 'Hide Detailed Information' : 'View More Reporter Details'}
                            <span className={`transform transition-transform duration-200 ${expandedProofDetails[proof.reporterId] ? 'rotate-180' : ''}`}>
                              ▼
                            </span>
                          </button>
                        </div>

                        {/* Expanded Detailed Information */}
                        {expandedProofDetails[proof.reporterId] && (
                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 shadow-inner">
                            <h5 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                              <FaUser className="w-5 h-5 text-blue-600" />
                              Complete Reporter Profile
                            </h5>

                            {/* Personal Information */}
                            <div className="mb-6">
                              <h6 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <FaUser className="w-4 h-4 text-blue-500" />
                                Personal Information
                              </h6>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-white rounded-lg p-3 border border-blue-200 shadow-sm">
                                  <label className="block text-xs font-medium text-blue-600 mb-1">Reporter ID</label>
                                  <p className="text-sm font-semibold text-gray-800">{proof.reporterId}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-green-200 shadow-sm">
                                  <label className="block text-xs font-medium text-green-600 mb-1">IINSAF ID</label>
                                  <p className="text-sm font-semibold text-gray-800">{proof.iinsafId || 'N/A'}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-purple-200 shadow-sm">
                                  <label className="block text-xs font-medium text-purple-600 mb-1">Channel Name</label>
                                  <p className="text-sm font-semibold text-gray-800">{proof.channelName || 'N/A'}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-orange-200 shadow-sm">
                                  <label className="block text-xs font-medium text-orange-600 mb-1">Platform</label>
                                  <p className="text-sm font-semibold text-gray-800">{proof.platform || 'N/A'}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-pink-200 shadow-sm">
                                  <label className="block text-xs font-medium text-pink-600 mb-1">Video Duration</label>
                                  <p className="text-sm font-semibold text-gray-800">{proof.duration || 'N/A'}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-indigo-200 shadow-sm">
                                  <label className="block text-xs font-medium text-indigo-600 mb-1">User Role</label>
                                  <p className="text-sm font-semibold text-gray-800">{proof.userRole || 'Reporter'}</p>
                                </div>
                              </div>
                            </div>

                            {/* Work Timeline */}
                            <div className="mb-6">
                              <h6 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <FaClock className="w-4 h-4 text-green-500" />
                                Work Timeline
                              </h6>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-lg p-3 border border-cyan-200 shadow-sm">
                                  <label className="block text-xs font-medium text-cyan-600 mb-1">Submitted At</label>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {proof.submittedAt ? new Date(proof.submittedAt).toLocaleString() : 'N/A'}
                                  </p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
                                  <label className="block text-xs font-medium text-slate-600 mb-1">Completed At</label>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {proof.adminApprovedAt ? new Date(proof.adminApprovedAt).toLocaleString() :
                                      proof.completionSubmittedAt ? new Date(proof.completionSubmittedAt).toLocaleString() : 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Reporter Payment & Timeline */}
                            <div className="mb-6">
                              <h6 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <FaMoneyBillWave className="w-4 h-4 text-green-500" />
                                Payment & Timeline
                              </h6>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-lg p-3 border border-blue-200 shadow-sm">
                                  <label className="block text-xs font-medium text-blue-600 mb-1">Payment Amount</label>
                                  <p className="text-lg font-bold text-blue-600">₹{selectedAd.finalReporterPrice ? Number(selectedAd.finalReporterPrice).toFixed(2) : '0.00'}</p>
                                  <p className="text-xs text-gray-500">Amount paid to reporter</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-green-200 shadow-sm">
                                  <label className="block text-xs font-medium text-green-600 mb-1">Work Status</label>
                                  <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                      <FaCheckCircle className="w-3 h-3 mr-1" />
                                      Work Completed
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">Proof approved by admin</p>
                                </div>
                              </div>

                              {/* Detailed Timeline */}
                              <div className="mt-4">
                                <label className="block text-xs font-medium text-gray-600 mb-2">Detailed Work Timeline</label>
                                <div className="space-y-2">
                                  {proof.submittedAt && (
                                    <div className="flex items-center gap-2 text-xs">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                      <span className="text-gray-600">Initial Proof Submitted:</span>
                                      <span className="font-medium">{new Date(proof.submittedAt).toLocaleString()}</span>
                                    </div>
                                  )}
                                  {proof.acceptedAt && (
                                    <div className="flex items-center gap-2 text-xs">
                                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                      <span className="text-gray-600">Proof Accepted:</span>
                                      <span className="font-medium">{new Date(proof.acceptedAt).toLocaleString()}</span>
                                    </div>
                                  )}
                                  {proof.completionSubmittedAt && (
                                    <div className="flex items-center gap-2 text-xs">
                                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                      <span className="text-gray-600">Completion Submitted:</span>
                                      <span className="font-medium">{new Date(proof.completionSubmittedAt).toLocaleString()}</span>
                                    </div>
                                  )}
                                  {proof.adminApprovedAt && (
                                    <div className="flex items-center gap-2 text-xs">
                                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                      <span className="text-gray-600">Admin Approved:</span>
                                      <span className="font-medium">{new Date(proof.adminApprovedAt).toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Proof Screenshots */}
                            <div className="mb-6">
                              <h6 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <FaCamera className="w-4 h-4 text-purple-500" />
                                Proof Screenshots
                              </h6>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {proof.screenshot && (
                                  <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                                    <label className="block text-xs font-medium text-gray-600 mb-2">Initial Proof</label>
                                    <img
                                      src={proof.screenshot.startsWith('http')
                                        ? proof.screenshot
                                        : `${process.env.REACT_APP_API_BASE_URL}/${proof.screenshot.replace(/\\/g, "/")}`}
                                      alt="Initial Proof"
                                      className="w-full h-40 object-cover rounded border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-200"
                                      onError={(e) => {
                                        e.target.src = "/fallback-image.jpg";
                                      }}
                                      onClick={() => {
                                        const imageUrl = proof.screenshot.startsWith('http')
                                          ? proof.screenshot
                                          : `${process.env.REACT_APP_API_BASE_URL}/${proof.screenshot.replace(/\\/g, "/")}`;
                                        setPreviewMedia({ url: imageUrl, type: 'image' });
                                        setShowPreviewModal(true);
                                      }}
                                    />
                                  </div>
                                )}
                                {proof.completedTaskScreenshot && (
                                  <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                                    <label className="block text-xs font-medium text-gray-600 mb-2">Completion Proof</label>
                                    <img
                                      src={proof.completedTaskScreenshot.startsWith('http')
                                        ? proof.completedTaskScreenshot
                                        : `${process.env.REACT_APP_API_BASE_URL}/${proof.completedTaskScreenshot.replace(/\\/g, "/")}`}
                                      alt="Completion Proof"
                                      className="w-full h-40 object-cover rounded border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-200"
                                      onError={(e) => {
                                        e.target.src = "/fallback-image.jpg";
                                      }}
                                      onClick={() => {
                                        const imageUrl = proof.completedTaskScreenshot.startsWith('http')
                                          ? proof.completedTaskScreenshot
                                          : `${process.env.REACT_APP_API_BASE_URL}/${proof.completedTaskScreenshot.replace(/\\/g, "/")}`;
                                        setPreviewMedia({ url: imageUrl, type: 'image' });
                                        setShowPreviewModal(true);
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              {proof.videoLink && (
                                <div className="mt-4 bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                                  <label className="block text-xs font-medium text-gray-600 mb-2">Video Link</label>
                                  <a
                                    href={proof.videoLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-sm underline break-all"
                                  >
                                    {proof.videoLink}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* ✅ Complete Work Submitted Details */}
                        {proof.proofDetails && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                <FaCheckCircle className="w-4 h-4 text-green-600" />
                                Submitted Work Details
                              </h4>
                              <button
                                onClick={() => {
                                  const reporterIdStr = typeof proof.reporterId === 'object' ? proof.reporterId._id : proof.reporterId;
                                  const key = `${reporterIdStr}-${proof.iinsafId}`;
                                  setExpandedProofDetails(prev => ({
                                    ...prev,
                                    [key]: !prev[key]
                                  }));
                                }}
                                className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                              >
                                <FaEye className="w-3 h-3" />
                                <span>
                                  {expandedProofDetails[`${typeof proof.reporterId === 'object' ? proof.reporterId._id : proof.reporterId}-${proof.iinsafId}`] ? 'Hide Details' : 'View Details'}
                                </span>
                              </button>
                            </div>

                            {/* ✅ Collapsible Proof Details */}
                            {expandedProofDetails[`${typeof proof.reporterId === 'object' ? proof.reporterId._id : proof.reporterId}-${proof.iinsafId}`] && (
                              <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Screenshot */}
                                  {proof.proofDetails.screenshot && (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-2">Screenshot</label>
                                      <div className="relative group">
                                        <img
                                          src={proof.proofDetails.screenshot}
                                          alt="Proof Screenshot"
                                          className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:shadow-md transition-all duration-200"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                          onClick={() => {
                                            // ✅ Handle both Cloudinary URLs and local paths
                                            const imageUrl = proof.proofDetails.screenshot.startsWith('http')
                                              ? proof.proofDetails.screenshot
                                              : `${process.env.REACT_APP_API_BASE_URL}/${proof.proofDetails.screenshot}`;
                                            setPreviewMedia({ url: imageUrl, type: 'image' });
                                            setShowPreviewModal(true);
                                          }}
                                        />
                                        <div className="absolute inset-0 bg-gray-100 rounded-lg border flex items-center justify-center" style={{ display: 'none' }}>
                                          <div className="text-center">
                                            <FaImage className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-xs text-gray-500">Image not available</p>
                                          </div>
                                        </div>
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                                          <FaEye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Video Link Button */}
                                  {proof.proofDetails.videoLink && (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-2">Video Link</label>
                                      <div className="w-full h-32 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200 flex items-center justify-center">
                                        <button
                                          onClick={() => window.open(proof.proofDetails.videoLink, '_blank')}
                                          className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                                        >
                                          <FaPlay className="w-4 h-4" />
                                          <span>Watch Video</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Channel and Platform Details */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Channel Name</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {proof.proofDetails.channelName || 'N/A'}
                                    </p>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {proof.proofDetails.platform || 'N/A'}
                                    </p>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {proof.proofDetails.duration || 'N/A'}
                                    </p>
                                  </div>
                                </div>

                                {/* Submission Details */}
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Submitted At</label>
                                      <p className="text-sm text-gray-800">
                                        {proof.proofDetails.submittedAt ? new Date(proof.proofDetails.submittedAt).toLocaleString() : 'N/A'}
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Proof Status</label>
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${proof.proofDetails.status === 'completed'
                                        ? 'bg-green-100 text-green-800 border border-green-200'
                                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                        }`}>
                                        <FaCheckCircle className="w-3 h-3 mr-1" />
                                        {proof.proofDetails.status || 'Unknown'}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Admin Reject Note (if any) */}
                                {proof.proofDetails.adminRejectNote && (
                                  <div className="mt-4 pt-4 border-t border-red-100">
                                    <label className="block text-xs font-medium text-red-600 mb-1">Admin Reject Note</label>
                                    <p className="text-sm text-red-800 bg-red-50 px-3 py-2 rounded-lg">
                                      {proof.proofDetails.adminRejectNote}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FaUsers className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">No completed reporters found</p>
                    <p className="text-sm text-gray-400 mt-1">Reporters who have completed their proofs will appear here</p>
                  </div>
                )}
              </div>

              {/* Financial Details Section */}
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaMoneyBillWave className="w-5 h-5 text-green-600" />
                  Financial Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Total Cost</label>
                    <p className="text-2xl font-bold text-green-600">₹{selectedAd.totalCost ? Number(selectedAd.totalCost).toFixed(2) : '0.00'}</p>
                    <p className="text-xs text-gray-500">Total advertisement cost</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Admin Commission</label>
                    <p className="text-2xl font-bold text-blue-600">₹{selectedAd.adminCommission ? Number(selectedAd.adminCommission).toFixed(2) : '0.00'}</p>
                    <p className="text-xs text-gray-500">Platform commission</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Reporter Rewards</label>
                    <p className="text-2xl font-bold text-purple-600">₹{selectedAd.finalReporterPrice ? Number(selectedAd.finalReporterPrice).toFixed(2) : '0.00'}</p>
                    <p className="text-xs text-gray-500">Per reporter payment</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-orange-200">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Total Reporters</label>
                    <p className="text-2xl font-bold text-orange-600">{selectedAd.requiredReporter || 0}</p>
                    <p className="text-xs text-gray-500">Required reporters</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Commission Rate</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-300"
                        style={{
                          width: `${selectedAd.adCommissionRate || 0}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      {selectedAd.adCommissionRate || 0}%
                    </span>
                  </div>
                </div>
              </div>



              {/* Refund Details Section */}
              {selectedAd.refundDetails && (
                <div className="bg-orange-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FaMoneyBillWave className="w-5 h-5 text-orange-600" />
                    Refund Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Refund Amount</label>
                      <p className="text-gray-800 font-semibold text-lg">₹{selectedAd.refundDetails.refundAmount}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Refund Reason</label>
                      <p className="text-gray-800">{selectedAd.refundDetails.refundReason}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Shortfall Reporters</label>
                      <p className="text-gray-800">{selectedAd.refundDetails.shortfallReporters} reporter(s)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Transaction ID</label>
                      <p className="text-gray-800 font-mono text-sm">{selectedAd.refundDetails.refundTransactionId}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Refunded At</label>
                      <p className="text-gray-800">{selectedAd.refundDetails.refundedAt ? new Date(selectedAd.refundDetails.refundedAt).toLocaleString() : 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps Section */}
              <div className="bg-gray-100 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Timestamps</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Created At</label>
                    <p className="text-gray-800">{selectedAd.createdAt ? new Date(selectedAd.createdAt).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Completed At</label>
                    <p className="text-gray-800">{selectedAd.completedAt ? new Date(selectedAd.completedAt).toLocaleString() : 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center p-6 border-t bg-gray-50 rounded-b-2xl">
              <div className="flex space-x-3">
                {selectedAd.status !== "completed" && (
                  <button
                    onClick={() => getCompletionDetails(selectedAd)}
                    className="flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <FaCheckCircle />
                    <span>Mark as Completed</span>
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaTimes />
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion Confirmation Modal */}
      {showCompletionModal && completionDetails && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-2xl">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FaCheckCircle />
                Complete Advertisement Confirmation
              </h2>
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletionDetails(null);
                  setShouldRefund(true); // Reset to default
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FaTimesCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Advertisement Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Advertisement Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Advertisement ID</label>
                    <p className="text-gray-800 font-mono">{completionDetails.adId}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Media Type</label>
                    <p className="text-gray-800">{completionDetails.advertisement?.mediaType || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Total Reporters</label>
                    <p className="text-gray-800">{completionDetails.totalReporters}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Completed Reporters</label>
                    <p className="text-gray-800">{completionDetails.completedReporters}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Incomplete Reporters</label>
                    <p className="text-gray-800">{completionDetails.incompleteReporters}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Completion Percentage</label>
                    <p className="text-gray-800">{completionDetails.completionPercentage}%</p>
                  </div>
                </div>
              </div>

              {/* Work Calculation Breakdown */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaCalculator className="text-blue-600" />
                  Work & Payment Calculation
                </h3>
                <div className="space-y-4">
                  {/* Total Budget */}
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Total Advertisement Budget</span>
                      <span className="text-lg font-bold text-blue-800">
                        ₹{completionDetails.totalReporters * (completionDetails.advertisement?.finalReporterPrice || 0)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {completionDetails.totalReporters} reporters × ₹{completionDetails.advertisement?.finalReporterPrice || 0} each
                    </div>
                  </div>

                  {/* Completed Work */}
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">✅ Completed Work (To be Paid)</span>
                      <span className="text-lg font-bold text-green-800">
                        ₹{completionDetails.completedReporters * (completionDetails.advertisement?.finalReporterPrice || 0)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {completionDetails.completedReporters} reporters completed work successfully
                    </div>
                  </div>

                  {/* Incomplete Work */}
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">❌ Incomplete Work (To be Refunded)</span>
                      <span className="text-lg font-bold text-red-800">
                        ₹{completionDetails.incompleteReporters * (completionDetails.advertisement?.finalReporterPrice || 0)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {completionDetails.incompleteReporters} reporters did not complete work
                    </div>
                  </div>

                  {/* Net Result */}
                  <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">💰 Net Result for Advertiser</span>
                      <span className="text-lg font-bold text-yellow-800">
                        ₹{completionDetails.incompleteReporters * (completionDetails.advertisement?.finalReporterPrice || 0)} Refund
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Advertiser pays only for completed work, gets refund for incomplete work
                    </div>
                  </div>
                </div>
              </div>

              {/* Refund Information */}
              {completionDetails.needsRefund && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FaExclamationTriangle className="text-red-500" />
                    Refund Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Total Refund Amount</label>
                      <p className="text-gray-800 font-semibold text-lg text-red-600">₹{completionDetails.totalRefundAmount}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Reporters to Refund</label>
                      <p className="text-gray-800">{completionDetails.incompleteReporters} reporter(s)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Amount Per Reporter</label>
                      <p className="text-gray-800 font-semibold">₹{completionDetails.advertisement?.finalReporterPrice || 0}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Refund Recipient</label>
                      <p className="text-gray-800">{completionDetails.advertisement?.owner?.name || 'Advertiser'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Refund Option - Admin Decision */}
              {completionDetails.needsRefund && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border-2 border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FaInfoCircle className="text-purple-600" />
                    Refund Decision
                  </h3>
                  <div className="space-y-4">
                    <p className="text-gray-700 text-sm">
                      Do you want to process the refund of <span className="font-bold text-purple-600">₹{completionDetails.totalRefundAmount}</span> to the Advertiser?
                    </p>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setShouldRefund(true)}
                        className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${shouldRefund
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg scale-105'
                          : 'bg-white text-gray-600 border-2 border-gray-300 hover:border-green-400'
                          }`}
                      >
                        <FaCheckCircle className={shouldRefund ? 'text-white' : 'text-green-500'} />
                        Yes, Process Refund
                      </button>
                      <button
                        onClick={() => setShouldRefund(false)}
                        className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${!shouldRefund
                          ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg scale-105'
                          : 'bg-white text-gray-600 border-2 border-gray-300 hover:border-red-400'
                          }`}
                      >
                        <FaTimesCircle className={!shouldRefund ? 'text-white' : 'text-red-500'} />
                        No, Skip Refund
                      </button>
                    </div>
                    {!shouldRefund && (
                      <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 rounded">
                        <p className="text-yellow-800 text-sm flex items-center gap-2">
                          <FaInfoCircle />
                          <span>The refund amount will NOT be credited to the Advertiser's wallet.</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reporter Details */}
              {completionDetails.reporterDetails && completionDetails.reporterDetails.length > 0 && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FaUsers className="text-yellow-600" />
                    Reporter Details
                  </h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {completionDetails.reporterDetails.map((reporter, index) => (
                      <div key={index} className="bg-white rounded-lg p-3 border border-yellow-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-gray-800">{reporter.reporterName}</p>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${reporter.shouldRefund
                                ? 'bg-red-100 text-red-800'
                                : 'bg-green-100 text-green-800'
                                }`}>
                                {reporter.shouldRefund ? 'Incomplete' : 'Completed'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{reporter.channelName} - {reporter.platform}</p>
                            <p className="text-xs text-gray-500">Status: {reporter.status}</p>
                            {reporter.refundReason && (
                              <p className="text-xs text-gray-600 mt-1 bg-gray-100 p-2 rounded">
                                {reporter.refundReason}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            {reporter.shouldRefund ? (
                              <div className="bg-red-50 p-2 rounded-lg border border-red-200">
                                <p className="text-sm font-semibold text-red-600">₹{reporter.refundAmount}</p>
                                <p className="text-xs text-red-500">Refund to Advertiser</p>
                              </div>
                            ) : (
                              <div className="bg-green-50 p-2 rounded-lg border border-green-200">
                                <p className="text-sm font-semibold text-green-600">₹{reporter.refundAmount}</p>
                                <p className="text-xs text-green-500">Paid to Reporter</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Final Summary</h3>
                <div className="space-y-3">
                  {/* Work Distribution */}
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <h4 className="font-semibold text-gray-800 mb-2">Work Distribution</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Reporters Assigned:</span>
                        <span className="font-semibold">{completionDetails.totalReporters}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">✅ Completed Work:</span>
                        <span className="font-semibold text-green-600">{completionDetails.completedReporters} reporter(s)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">❌ Incomplete Work:</span>
                        <span className="font-semibold text-red-600">{completionDetails.incompleteReporters} reporter(s)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Completion Rate:</span>
                        <span className="font-semibold text-blue-600">{completionDetails.completionPercentage}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <h4 className="font-semibold text-gray-800 mb-2">Financial Summary</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Budget:</span>
                        <span className="font-semibold">₹{completionDetails.totalReporters * (completionDetails.advertisement?.finalReporterPrice || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount to Reporters:</span>
                        <span className="font-semibold text-green-600">₹{completionDetails.completedReporters * (completionDetails.advertisement?.finalReporterPrice || 0)}</span>
                      </div>
                      {completionDetails.needsRefund && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            {shouldRefund ? "Refund to Advertiser:" : "Refund (Skipped):"}
                          </span>
                          <span className={`font-semibold ${shouldRefund ? 'text-orange-600' : 'text-gray-400 line-through'}`}>
                            ₹{completionDetails.totalRefundAmount}
                          </span>
                        </div>
                      )}
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-semibold">Advertiser Savings:</span>
                          <span className="font-semibold text-green-600">
                            ₹{shouldRefund && completionDetails.needsRefund ? completionDetails.totalRefundAmount : 0}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {shouldRefund ? "Advertiser pays only for completed work and gets refund" : "Advertiser pays for all assigned reporters (no refund)"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center p-6 border-t bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletionDetails(null);
                  setShouldRefund(true); // Reset to default
                }}
                className="flex items-center space-x-2 bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaTimes />
                <span>Cancel</span>
              </button>
              <button
                onClick={confirmCompletion}
                disabled={completionLoading}
                className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completionLoading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    <span>Confirm Completion</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletedPost;

